import { useCallback, useEffect, useRef } from 'react';

const IDLE_TIMEOUT_MS = 10 * 60 * 1000;
const STORAGE_KEY = 'coremanage_last_activity';
const EVENTS = ['mousemove', 'keydown', 'click', 'scroll'] as const;

export default function useIdleTimeout(onIdle: () => void, enabled = true): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const onIdleRef = useRef(onIdle);

  useEffect(() => {
    onIdleRef.current = onIdle;
  });

  const resetTimer = useCallback(() => {
    if (!enabled) return;
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onIdleRef.current(), IDLE_TIMEOUT_MS);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;

    resetTimer();
    const handler = () => resetTimer();
    EVENTS.forEach((eventName) => window.addEventListener(eventName, handler, { passive: true }));

    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY && event.newValue) {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => onIdleRef.current(), IDLE_TIMEOUT_MS);
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      clearTimeout(timerRef.current);
      EVENTS.forEach((eventName) => window.removeEventListener(eventName, handler));
      window.removeEventListener('storage', onStorage);
    };
  }, [enabled, resetTimer]);
}